import { Dialog, HTMLTable } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import React, { FC, useMemo } from 'react'
import ProductWithThumbnailLink from '../../../../reports/components/ProductWithThumbnailLink'
import { formatDate, formatMoney } from '../../../../../utils'
import { UprightStyle } from '../../../../../types'
import { PriceRecommendations } from './types'
import { getMinimumThresholdPrices, getMinimumThresholdResults } from './Helpers'

interface PriceRecommendationProps {
  onClose?: (averages?: any) => void
  showPricePredictionsDialog: boolean
  orgPredictions: PriceRecommendations[]
  globalPredictions: PriceRecommendations[]
  query: any
  showBINPrice?: boolean
  showStartingPrice?: boolean
}
export const MAX_RECOMMENDATIONS_TO_SHOW = 5

export const styles: UprightStyle = {
  averagePriceSummary: {
    background: '#EBF1F5',
    fontSize: '18px',
    maxHeight: '60px'
  },
  dialogHeading: {
    background: '#EBF1F5',
    paddingLeft: '20px',
    paddingRight: '5px',
    alignItems: 'center',
    display: 'flex',
    fontSize: '18px',
    maxHeight: '30px'
  },
  dialog: {
    width: '700px',
    background: '#EBF1F5'
  },
  recommendationTable: {
    display: 'block',
    overflow: 'auto',
    maxHeight: 500
  },
  halfRecommendationTable: {
    display: 'block',
    overflow: 'auto',
    maxHeight: 250
  },
  truncate: {
    maxWidth: '100%',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  bolderText: {
    fontWeight: 'bolder'
  }
}

export const PriceRecommendationDialog: FC<PriceRecommendationProps> = props => {
  const { showBINPrice, showStartingPrice, orgPredictions, globalPredictions } = props

  const meanValue = useMemo(() => {
    const topTenPredictions = getMinimumThresholdPrices(orgPredictions, MAX_RECOMMENDATIONS_TO_SHOW)
    const topTenGlobalPredictions = globalPredictions
      ? getMinimumThresholdPrices(globalPredictions, MAX_RECOMMENDATIONS_TO_SHOW)
      : []
    const allResults = [...topTenGlobalPredictions, ...topTenPredictions]
    const sum = allResults.reduce((acc, price) => {
      return acc + price
    }, 0)
    return parseFloat((sum / allResults.length).toFixed(2))
  }, [orgPredictions, globalPredictions])

  const startingMeanValue = useMemo(() => {
    const topTenPredictions = getMinimumThresholdPrices(orgPredictions, MAX_RECOMMENDATIONS_TO_SHOW, true)
    const sum = topTenPredictions.reduce((acc, price) => {
      return acc + price
    }, 0)
    return parseFloat((sum / topTenPredictions.length).toFixed(2))
  }, [orgPredictions])

  const binMeanValue = useMemo(() => {
    const topTenPredictions = getMinimumThresholdPrices(orgPredictions, MAX_RECOMMENDATIONS_TO_SHOW, false, true)
    const sum = topTenPredictions.reduce((acc, price) => {
      return acc + price
    }, 0)
    return parseFloat((sum / topTenPredictions.length).toFixed(2))
  }, [orgPredictions])

  const orgResults = getMinimumThresholdResults(orgPredictions || []).slice(0, MAX_RECOMMENDATIONS_TO_SHOW)
  const globalResults = getMinimumThresholdResults(globalPredictions || []).slice(0, MAX_RECOMMENDATIONS_TO_SHOW)

  const closeModal = () => {
    // analytics tracking
    props.onClose({
      name: 'averagePredictions',
      value: { buyItNowPrice: binMeanValue, mean: meanValue, startingPrice: startingMeanValue }
    })
  }

  return (
    <Dialog
      icon={IconNames.SERIES_SEARCH}
      isOpen={props.showPricePredictionsDialog}
      title="Historical Listings"
      onClose={() => {
        closeModal()
      }}
      style={styles.dialog}
      autoFocus={false}
      portalClassName="upright-popover-safe-dialog"
    >
      <div className="bp3-dialog-header" style={styles.averagePriceSummary}>
        <h4> Average Price:&nbsp;{formatMoney(meanValue.toString())}</h4>&nbsp;&nbsp; {' Sold for Price '}
        {showStartingPrice && startingMeanValue > 0 && (
          <>
            &nbsp;|&nbsp; <span style={styles.bolderText}>{formatMoney(startingMeanValue.toString())}</span>&nbsp;
            Starting Price &nbsp;
          </>
        )}
        {showBINPrice && binMeanValue > 0 && (
          <>
            &nbsp;|&nbsp; <span style={styles.bolderText}>{formatMoney(binMeanValue.toString())}</span>&nbsp;BIN Price
            &nbsp;
          </>
        )}
      </div>

      <hr />

      <div style={styles.dialogHeading}>
        <h4>Matching Listings</h4>
      </div>
      <div className="bp3-dialog-body price-prediction" style={{ margin: '10px' }}>
        <HTMLTable
          style={globalResults?.length > 0 ? styles.halfRecommendationTable : styles.recommendationTable}
          interactive
          condensed
        >
          <th>Image</th>
          <th>Title</th>
          <th> </th>
          <th>Sold For</th>
          <th>Listing Type</th>
          <th>Date Sold</th>
          <th>Condition</th>
          <tr style={{ borderTop: '1px solid' }}>
            <td colSpan={7}>
              <hr />
            </td>
          </tr>

          {orgResults.map(priceRecommendation => {
            return (
              <tr key={priceRecommendation.productId}>
                <td style={{ width: '15%' }}>
                  <ProductWithThumbnailLink
                    productId={priceRecommendation.productId}
                    thumbnailURL={priceRecommendation.thumbnailImageUrl}
                    style={{ padding: 5, width: 65, height: 65, paddingRight: 0 }}
                  />
                </td>
                <td colSpan={2} style={{ width: '35%' }}>
                  <a
                    target="_blank"
                    href={`/products/${priceRecommendation.productId}`}
                    style={styles.truncate}
                    rel="noreferrer"
                  >
                    {priceRecommendation.title}
                  </a>
                </td>
                <td>{formatMoney(priceRecommendation.price)}</td>
                <td className="joined-form channel-title">{priceRecommendation.listingType}</td>
                <td className="joined-form channel-title">
                  {formatDate(new Date(priceRecommendation.orderItemCreatedAt))}
                </td>
                <td>
                  <span style={styles.truncate}>{priceRecommendation.condition || '-'}</span>
                </td>
              </tr>
            )
          })}
        </HTMLTable>
      </div>
      {globalResults?.length > 0 && (
        <>
          <div style={styles.dialogHeading}>
            <h4>Aggregate Listings</h4>
          </div>

          <div className="bp3-dialog-body price-prediction" style={{ margin: '10px' }}>
            <HTMLTable
              style={styles.halfRecommendationTable}
              interactive
              striped // doesn't work.
              condensed
            >
              <th>Matched Keywords</th>
              <th> </th>
              <th> </th>
              <th>Sold For</th>
              <th>Listing Type </th>
              <th>Date Sold</th>
              <th>Condition</th>
              <tr style={{ borderTop: '1px solid' }}>
                <td colSpan={7}>
                  <hr />
                </td>
              </tr>

              {globalResults.map(priceRecommendation => {
                return (
                  <tr>
                    <td style={{ width: '50%' }} colSpan={2}>
                      {priceRecommendation.title || '-'}
                    </td>
                    <td />
                    <td>{formatMoney(priceRecommendation.price)}</td>
                    <td className="joined-form channel-title">{priceRecommendation.listingType || '-'}</td>
                    <td className="joined-form channel-title">-</td>
                    <td>
                      <span style={styles.truncate}>{priceRecommendation.condition || '-'}</span>
                    </td>
                  </tr>
                )
              })}
            </HTMLTable>
          </div>
        </>
      )}
    </Dialog>
  )
}
